import { InlineAlert } from '../../components/BaseComponents'
import { ReactNode, useEffect, useState } from "react";
import { Button, LoadingSpinner, SearchableSelect, TextArea } from "../../components/BaseComponents";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import CurrencyInput from "../../components/BaseComponents/Forms/CurrencyInput";
import useApi from "../../hooks/useApi";
import { currencyToCents, getApiErrorMessage, getCSRFToken } from "../../lib/utils";
import { CurrencyCode } from "../../types/currency";
import { PCAF_INSTRUMENT_CATEGORIES } from "../../lib/pcafInstrumentCategories.generated";

type PortfolioPcafAllocationModalProps = {
    onClose: () => void,
    onSaved: () => void,
    isOpen?: boolean,
    portfolioId: string,
    companyId: string,
    companyName: string,
};

// Renders **bold** and *italic* markdown spans from docs/todo/pcaf-help-for-10-categories.md as JSX.
function renderInlineMarkdown(text: string): ReactNode {
    return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean).map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={index}>{part.slice(1, -1)}</em>;
        }
        return part;
    });
}

// PCAF instrument categories per the 2025 Financed Emissions Standard (Part A). The category list,
// order and denominator labels come from shared/pcafInstrumentCategories.json (also used by the backend
// validator); only the help text lives here. The denominator label is one entry per display line -
// the stored/API denominatorType joins these lines back into a single string.
// helpText is one entry per bullet point, transcribed from docs/todo/pcaf-help-for-10-categories.md
// (PCAF 2025 3rd Edition guidance), rendered under a shared "Summary of PCAF guidance" heading -
// the source doc's per-category heading is omitted since the dropdown selection already makes it clear.
const CATEGORY_HELP: { value: string, helpText: string[] }[] = [
    {
        value: 'Listed equity and corporate bonds',
        helpText: [
            'EVIC: use **market cap of shares** + **total debt** (all instruments incl. preferred shares + minority interests) with no deduction of cash or equivalent',
            'Do *not* use **book equity** or **purchase price**. EVIC uses **market cap** as the equity component.',
            '**Outstanding amount**: for equity, use current market value of holding; for bonds, outstanding principal/book value held (not purchase price or fair value)',
            'Attribution factor = **bank\'s holding** ÷ **EVIC** and applies identically to both bonds and equity.',
            'When a financial institution holds both bonds *and* equity in the same issuer, calculate and report them separately, then sum at portfolio level.',
        ],
    },
    {
        value: 'Business loans and unlisted equity',
        helpText: [
            'Denominator: use **total book value of equity** + **debt** of the borrower (no EVIC, no market cap. Unlisted companies have no observable market value for equity).',
            'Do *not* substitute EVIC or market cap for the denominator. Private companies require book equity + book debt instead.',
            '**Outstanding amount** (loans): use actual outstanding loan balance (disbursed amount minus repayments), *not* committed facility/limit amount.',
            '**Outstanding amount** (unlisted equity): calculated as the institution\'s % share of total shares × total equity per balance sheet, not purchase price or fair value.',
            'Attribution factor = **outstanding amount** ÷ (**total equity** + **total debt**). This applies identically whether the exposure is a loan or an unlisted equity stake.',
            'If total company equity is *negative* per the balance sheet, floor it to zero. All emissions are then attributed to debt only, none to equity.',
            'When holding both a loan *and* equity in the same unlisted entity, calculate and report each exposure separately, then sum at portfolio level.',
        ],
    },
    {
        value: 'Project finance',
        helpText: [
            'Denominator: use **total project equity** + **debt** available to the project. This is the total financing structure, *not* the project\'s asset/book value.',
            'At project start, the denominator is total financing committed; once operational, use the *actual* equity and debt drawn to date, not the original committed amount.',
            '**Outstanding amount**: the bank\'s actual outstanding loan/equity contribution to the project (not committed/undrawn facility).',
            'Attribution factor = **bank\'s outstanding amount** ÷ (**total project equity** + **total project debt**).',
            'Report Scope 1 and Scope 2 of the project as *absolute*, required figures; Scope 3 is reported separately and is *not* combined into a single blended total.',
            'Where a bank holds *both* a loan and equity in the same project, calculate and report each separately, then sum at portfolio level.',
        ],
    },
    {
        value: 'Commercial real estate',
        helpText: [
            'Denominator: use **property valuation at origination** (or latest valuation if origination data unavailable), *not* latest/current market valuation.',
            '**Outstanding amount** = loan balance at reporting date (do *not* use original committed facility or undrawn amount).',
            'Attribution factor = **bank\'s outstanding loan** ÷ **property value at origination**. Applies per property/per SPV loan.',
            'For single-property SPVs, shareholder equity on the SPV balance sheet is *not* part of the attribution denominator.',
            'Benchmark energy intensity data (e.g. kWh/m² comparator databases) should *not* be used when actual metered consumption data is available.',
            'Total lettable floor area is only relevant where it feeds Scope 3 estimation methodology (e.g. embodied CO₂e per m²), not as Scope 1/2 data.',
        ],
    },
    {
        value: 'Mortgages',
        helpText: [
            'Denominator: use **property value at origination** fixed for the life of the loan and *not* re-marked when house prices rise or fall.',
            'Only use current/latest valuation as a substitute if the origination value genuinely cannot be sourced.',
            '**Outstanding amount** = actual outstanding mortgage balance at reporting date, *not* the original loan amount or credit limit.',
            'Attribution factor = **outstanding mortgage balance** ÷ **property value at origination**.',
            'Emissions factors should be selected by property type and location; use EPC rating/energy label where available rather than a generic national average.',
            'Do *not* use benchmark/comparator energy intensity data where actual metered consumption exists (same principle as commercial real estate).',
        ],
    },
    {
        value: 'Motor vehicle loans',
        helpText: [
            'Denominator: use **total value of the vehicle at loan origination** (the purchase price at the time of the transaction), *not* current resale/market value.',
            '**Outstanding amount** = the actual outstanding motor vehicle loan balance owed by the debtor, *not* the original loan amount.',
            'Attribution factor = **outstanding loan amount** ÷ **vehicle value at origination**.',
            'Emissions should be calculated from vehicle-specific data (fuel type, make/model) combined with estimated or actual annual distance driven. Use *actual* mileage where telematics/odometer data exists, in preference to national average mileage assumptions.',
            'For loan books with many vehicles, an even/average allocation approach *may* be used only where individual vehicle-level data is genuinely unavailable. This is a fallback, not the preferred method.',
        ],
    },
    {
        value: 'Use of proceeds structures',
        helpText: [
            'Applies to green bonds, sustainability-linked loans, and any instrument where proceeds are explicitly ring-fenced to a specific project or use and *not* general corporate purpose lending.',
            'Denominator switches from the issuer\'s aggregate EVIC/book value to the **total cost** of the specific financed project/object (e.g. total capex).',
            '**Outstanding amount** = the bank\'s exposure specifically allocated to that project/use, *not* the issuer\'s total outstanding debt.',
            'Object-value attribution factor = **outstanding loan amount allocated to project** ÷ **total project cost**. This is calculated *per financed object*, then aggregated.',
            'Distinguish *separate* structures (financed emissions arise only once funds are actually allocated) from *integrated* structures (both allocated and unallocated amounts must be considered).',
            'Where proceeds cannot be traced to a specific use, treat the exposure under the general corporate methodology for debt or equity instead, *not* under use-of-proceeds rules.',
        ],
    },
    {
        value: 'Securitization and structured products',
        helpText: [
            'Applies to RMBS, CMBS, ABS, CLOs and similar tranche/pool structures. Requires "look-through" to the underlying assets, *not* the structure\'s headline issuance value.',
            'Neoni records a single attribution factor: **bank\'s outstanding tranche holding** ÷ **total value of the securitised pool**. Multi-level (collateral, loan, tranche, investment) attribution is not supported yet.',
            '**Outstanding amount** = the bank\'s actual outstanding tranche holding, net of any credit losses allocated to that tranche (principal deficiency ledger write-downs must be deducted).',
            'Issuers calculate emissions for the *full* structure; investors calculate only their attributed share. Do not conflate the two roles.',
            'Where granular loan-level look-through data is unavailable, this asset class typically carries a *high* data-quality challenge. Flag this and disclose it as a limitation rather than defaulting silently to a proxy.',
        ],
    },
    {
        value: 'Sovereign debt',
        helpText: [
            'Denominator for attribution = country\'s **PPP**-adjusted GDP (*not* nominal GDP, *not* total government debt outstanding).',
            'Attribution factor = **bank\'s sovereign bond holding** ÷ **country\'s PPP-adjusted GDP**. Neoni does not cap the factor at 1 automatically, so check the outstanding amount does not exceed the denominator.',
            'Sovereign emissions are reported as Scope 1, 2 and 3 separately, *not* as a single blended figure; record the country as the company and enter its emissions as the report figures.',
            'Exclude central bank holdings for monetary policy purposes from your reported sovereign exposure. Use only your bank\'s own bond holding.',
        ],
    },
    {
        value: 'Sub-sovereign debt',
        helpText: [
            'Denominator = sub-sovereign\'s **PPP**-adjusted GDP; since this is *not* typically published at regional level, apply the *country\'s* **PPP** adjustment factor to the sub-sovereign\'s nominal GDP.',
            '**Outstanding amount** = actual outstanding loan or bond amount the sub-sovereign is obligated to repay (disbursed debt minus repayments), *not* the original issuance amount.',
            'Attribution factor = **bank\'s outstanding exposure** ÷ **sub-sovereign\'s PPP-adjusted GDP**. Neoni does not cap the factor at 1 automatically.',
            'Emissions boundary mirrors sovereign debt (Scope 1/2/3 reported separately) but scoped to the region\'s own territorial inventory, *not* the national inventory.',
        ],
    },
]

const INSTRUMENT_CATEGORIES: { value: string, denominatorLabel: string[], helpText: string[] }[] =
    PCAF_INSTRUMENT_CATEGORIES.map(category => ({
        value: category.name,
        denominatorLabel: category.denominatorLabel,
        helpText: CATEGORY_HELP.find(help => help.value === category.name)?.helpText ?? [],
    }))

const INSTRUMENT_CATEGORY_OPTIONS = INSTRUMENT_CATEGORIES.map((category, index) => ({
    value: category.value,
    label: `${index + 1}. ${category.value}`,
}))

function PortfolioPcafAllocationModal({
    onClose,
    onSaved,
    isOpen,
    portfolioId,
    companyId,
    companyName,
}: PortfolioPcafAllocationModalProps) {
    const api = useApi();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // Separate from `error` (used for save/remove failures, shown alongside the form): a load
    // failure means there is no known-good allocation to show or act on at all, so it replaces
    // the form/actions entirely rather than being layered on top of stale/default-looking data.
    const [loadError, setLoadError] = useState('');
    // Bumped by the Retry button below to re-run the load effect without touching isOpen/ids.
    const [reloadToken, setReloadToken] = useState(0);

    const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('GBP');
    const [instrumentCategory, setInstrumentCategory] = useState('');
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [denominatorAmount, setDenominatorAmount] = useState(0);
    const [methodologyNotes, setMethodologyNotes] = useState('');
    const [savedAttributionFactor, setSavedAttributionFactor] = useState(1);
    const [showHelp, setShowHelp] = useState(false);
    const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

    // Issue #884: this GET is reachable while assuming another organisation and can reject with
    // ACCESS_AUDIT_UNAVAILABLE (#857 item 6), as well as any generic failure. Previously there
    // was no try/catch at all, so a rejection left `loading` stuck true forever (an endless
    // spinner, with Save/Remove/Cancel all hidden since `actions` renders null while loading) and
    // became an uncaught promise rejection. Track it as its own error so the modal can show a
    // real, visible failure with a retry instead of hanging or displaying old/default-looking
    // form data with Save/Remove wrongly available.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        setLoadError('');
        const run = async () => {
            try {
                const result = await api.get(`/portfolios/${portfolioId}/companies/${companyId}/pcafAllocation`);
                if (cancelled) {
                    return;
                }
                const { data } = result;
                setCurrencyCode(data.currencyCode);
                setInstrumentCategory(data.instrumentCategory || '');
                setOutstandingAmount(Number(data.outstandingAmount) || 0);
                setDenominatorAmount(Number(data.denominatorAmount) || 0);
                setMethodologyNotes(data.methodologyNotes || '');
                setSavedAttributionFactor(data.attributionFactor);
                setLoading(false);
            } catch (e) {
                if (cancelled) {
                    return;
                }
                setLoadError(getApiErrorMessage(e, 'Could not load this allocation. Please try again.'));
                setLoading(false);
            }
        }
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, portfolioId, companyId, reloadToken]);

    const selectedCategory = INSTRUMENT_CATEGORIES.find(category => category.value === instrumentCategory);
    const isValid = !!instrumentCategory && denominatorAmount > 0 && outstandingAmount > 0;

    // Bad/incomplete input never corrupts the display - it just keeps showing the saved value.
    const attributionFactor = isValid ? outstandingAmount / denominatorAmount : savedAttributionFactor;

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            await api.put(`/portfolios/${portfolioId}/companies/${companyId}/pcafAllocation`, {
                instrumentCategory,
                outstandingAmount,
                denominatorType: selectedCategory?.denominatorLabel.join(' '),
                denominatorAmount,
                methodologyNotes: methodologyNotes || undefined,
                _csrf: await getCSRFToken(),
            });
            onSaved();
        } catch (e) {
            setError('Could not save this allocation. Please check your inputs and try again.');
        } finally {
            setSaving(false);
        }
    }

    const removeAllocation = async () => {
        setRemoveConfirmOpen(false);
        setSaving(true);
        setError('');
        try {
            await api.delete(`/portfolios/${portfolioId}/companies/${companyId}/pcafAllocation`, {
                _csrf: await getCSRFToken(),
            });
            onSaved();
        } catch (e) {
            setError('Could not remove this allocation. Please try again.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <CanonicalModal
                open={!!isOpen}
                onClose={onClose}
                size="small"
                title="Allocate emissions"
                subtitle={companyName}
                actions={!(loading || saving) && (
                    loadError ? (
                        <>
                            <Button onClick={() => setReloadToken(token => token + 1)} additionalClass="mr-5" type="Primary" text="Retry" />
                            <Button onClick={onClose} type="Outlined" text="Cancel" />
                        </>
                    ) : (
                        <>
                            <Button onClick={save} additionalClass="mr-5" disabled={!isValid} type="Primary" text="Save" />
                            <Button onClick={() => setRemoveConfirmOpen(true)} additionalClass="mr-5" type="Outlined" text="Remove allocation" />
                            <Button onClick={onClose} type="Outlined" text="Cancel" />
                        </>
                    )
                )}
            >
                { loading || saving ? (
                    <LoadingSpinner />
                ) : loadError ? (
                    <InlineAlert type='error' className='mt-6'>{ loadError }
                    </InlineAlert>
                ) : (
                    <>
                        { instrumentCategory && selectedCategory && (
                            <div className="rounded-lg bg-sun-500 p-4 mt-8">
                                <div className={`rounded-lg bg-white p-4 mb-4 text-charcoal-800 text-sm ${!showHelp && 'hidden'}`} id="pcafHelpSection" data-testid="pcaf-help-section">
                                    <p className="mb-2">Summary of PCAF guidance (2025, 3rd Edition)</p>
                                    <ul className="list-disc pl-5 space-y-1">
                                        { selectedCategory.helpText.map((line, index) => (
                                            <li key={index}>{renderInlineMarkdown(line)}</li>
                                        )) }
                                    </ul>
                                </div>
                                <Button
                                    type="Primary"
                                    text={showHelp ? 'Hide information' : 'What information is required?'}
                                    hasLeadingIcon
                                    size="sm"
                                    icon={`fa-solid ${showHelp ? 'fa-minus' : 'fa-plus'}`}
                                    onClick={() => setShowHelp(!showHelp)}
                                    ariaControls="pcafHelpSection"
                                    ariaExpanded={showHelp}
                                />
                            </div>
                        )}

                        <label className="mt-8 block text-charcoal-100">
                            PCAF instrument category
                            <SearchableSelect
                                className="mt-1"
                                fillContainer
                                aria-label="PCAF instrument category"
                                isSearchable={false}
                                placeholder="Select category..."
                                options={INSTRUMENT_CATEGORY_OPTIONS}
                                value={INSTRUMENT_CATEGORY_OPTIONS.find((option) => option.value === instrumentCategory)}
                                onChange={(option) => {
                                    setInstrumentCategory(option?.value ?? '');
                                    setShowHelp(false);
                                }}
                            />
                        </label>

                        { instrumentCategory && (
                            <>
                                <label className="mt-4 block text-charcoal-100" data-testid="pcaf-outstanding-amount">
                                    Outstanding amount
                                    <CurrencyInput
                                        name="outstandingAmount"
                                        currencyCode={currencyCode}
                                        defaultValue={outstandingAmount}
                                        onChange={value => setOutstandingAmount(currencyToCents(value))}
                                    />
                                </label>
                                <label className="mt-4 block text-charcoal-100" data-testid="pcaf-denominator-amount">
                                    { selectedCategory?.denominatorLabel.map(line => (
                                        <span className="block" key={line}>{line}</span>
                                    )) || 'Denominator amount' }
                                    <CurrencyInput
                                        name="denominatorAmount"
                                        currencyCode={currencyCode}
                                        defaultValue={denominatorAmount}
                                        onChange={value => setDenominatorAmount(currencyToCents(value))}
                                    />
                                </label>

                                <div className="mt-4 text-charcoal-100">
                                    Attribution factor: <span>{ (attributionFactor * 100).toFixed(2) }%</span>
                                </div>
                                { (outstandingAmount <= 0 || denominatorAmount <= 0) && (
                                    <div className="mt-2 text-sm text-charcoal-100" data-testid="pcaf-zero-amount-hint">
                                        Both amounts must be greater than zero.
                                    </div>
                                )}

                                <label className="mt-4 block text-charcoal-100">
                                    Methodology notes (optional)
                                    <TextArea
                                        className="mt-1"
                                        value={methodologyNotes}
                                        onChange={e => setMethodologyNotes(e.target.value)}
                                        placeholder="Add comments, assumptions, or other relevant information."
                                    />
                                </label>
                            </>
                        )}

                        { error && (
                            <InlineAlert type='error' className='mt-6'>{ error }
                            </InlineAlert>
                        )}
                    </>
                )}
            </CanonicalModal>
            <ConfirmationModal
                open={removeConfirmOpen}
                onCancel={() => setRemoveConfirmOpen(false)}
                onConfirm={removeAllocation}
                title="Remove allocation?"
            >
                <p className="text-white mt-8">Remove this allocation and revert to default?</p>
            </ConfirmationModal>
        </>
    )
}

export default PortfolioPcafAllocationModal;
